import { CLIENT_STATUS_TRANSITIONS, type ClientStatus } from '@homecare/domain';
import { Button, Field } from '@homecare/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi, useRefreshClients } from '../../lib/api-hooks';

/**
 * Moving a client through the status machine (§4).
 *
 * ## What was missing
 *
 * The machine, `POST /v1/clients/:id/status`, and `client_status_history` all
 * worked and were tested, and no human could reach any of them. A client
 * created through intake was `REFERRAL` and stayed `REFERRAL` for ever: the
 * roster filtered across five statuses the console could produce one of, and
 * the history table recorded changes nobody could make. Exit criterion 2 asks
 * for referral → intake → active with a history row for each, and the product
 * could not do it in front of anybody.
 *
 * ## Only legal moves are offered
 *
 * The buttons are generated from `CLIENT_STATUS_TRANSITIONS`, the same table
 * the API validates against — not a list retyped here. A move the machine would
 * refuse is not rendered, so the common case never produces an error, and the
 * uncommon one (two tabs open, somebody else moved the client first) still gets
 * the API's refusal rather than a client-side guess at it.
 *
 * Discharge is deliberately **not** among them even though the table permits it
 * from every state. It ends an episode of care and stops billing, it holds its
 * own permission (`client:discharge`, separate from `client:update` in §10.4),
 * and it takes a date rather than an instant. It is a different act and it gets
 * a different control.
 */

/** Verbs, keyed by the move rather than by the destination. */
const MOVE_LABEL: Record<string, string> = {
  'REFERRAL->INTAKE': 'Start intake',
  'INTAKE->ACTIVE': 'Start of care',
  'ACTIVE->ON_HOLD': 'Put on hold',
  'ON_HOLD->ACTIVE': 'Resume care',
};

/** What the reason is for, said differently per move because it is. */
const MOVE_HINT: Record<string, string> = {
  'REFERRAL->INTAKE': 'Who referred them, and what was agreed.',
  'INTAKE->ACTIVE': 'What confirms the start of care — the signed agreement, the first visit.',
  'ACTIVE->ON_HOLD': 'Why care is pausing, and when it is expected to resume.',
  'ON_HOLD->ACTIVE': 'What has changed since the hold.',
};

export interface StatusMove {
  to: ClientStatus;
  label: string;
  hint: string;
}

/**
 * The moves a person may make from here, discharge excluded.
 *
 * Pure and exported: the mapping from a status to a set of buttons is the part
 * worth asserting, and asserting it does not need a browser.
 */
export function legalMoves(from: ClientStatus): StatusMove[] {
  return (CLIENT_STATUS_TRANSITIONS[from] ?? [])
    .filter((to) => to !== 'DISCHARGED')
    .map((to) => {
      const key = `${from}->${to}`;
      return {
        to,
        label: MOVE_LABEL[key] ?? `Move to ${to.toLowerCase().replace('_', ' ')}`,
        hint: MOVE_HINT[key] ?? 'Say why. It goes on the record with your name.',
      };
    });
}

export function StatusActions({
  clientId,
  status,
  canUpdate,
  canDischarge,
}: {
  clientId: string;
  status: ClientStatus;
  canUpdate: boolean;
  canDischarge: boolean;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const refreshClients = useRefreshClients();

  const [open, setOpen] = useState<StatusMove | 'discharge' | null>(null);
  const [reason, setReason] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const moves = canUpdate ? legalMoves(status) : [];
  const dischargeable = canDischarge && status !== 'DISCHARGED';

  if (moves.length === 0 && !dischargeable) return null;

  const begin = (next: StatusMove | 'discharge'): void => {
    setOpen(next);
    setReason('');
    setEffectiveAt('');
    setFailure(null);
  };

  const submit = async (): Promise<void> => {
    if (!open) return;

    setSaving(true);
    setFailure(null);

    try {
      if (open === 'discharge') {
        await api.send('POST', `/v1/clients/${clientId}/discharge`, {
          reason: reason.trim(),
          // Omitted rather than sent empty: the API defaults to today, and
          // backdating is common because care usually stops before anybody
          // gets to the console to record it.
          ...(effectiveAt ? { effectiveAt } : {}),
        });
      } else {
        await api.send('POST', `/v1/clients/${clientId}/status`, {
          toStatus: open.to,
          reason: reason.trim(),
        });
      }

      // The client, its history, and the roster — the status shows in all
      // three, and a stale pill beside a fresh history reads as a failed save.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] }),
        queryClient.invalidateQueries({ queryKey: ['clients', 'history', clientId] }),
        refreshClients(),
      ]);

      setOpen(null);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const discharging = open === 'discharge';

  return (
    <div className="grid gap-3" data-testid="status-actions">
      <div className="flex flex-wrap items-center gap-2">
        {moves.map((move) => (
          <Button key={move.to} size="sm" onClick={() => begin(move)}>
            {move.label}
          </Button>
        ))}

        {dischargeable && (
          // Secondary, not danger. Discharge is an ordinary end to an ordinary
          // episode of care — most clients are discharged eventually — and the
          // critical treatment belongs to things that have gone wrong.
          <Button variant="secondary" size="sm" onClick={() => begin('discharge')}>
            Discharge
          </Button>
        )}
      </div>

      {open && (
        <div className="border-line bg-surface-2 grid gap-3 rounded-md border p-4">
          <ErrorNote error={failure} nextStep="Check the reason and try again." />

          <Field
            label="Reason"
            // Required by the endpoint and by `client_status_history.reason`,
            // which is NOT NULL. Asked for here rather than defaulted, because
            // a default would put the same sentence on every client's record
            // and make the column worthless to the auditor it exists for.
            hint={discharging ? 'Why care is ending. It goes on the record.' : open.hint}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            data-testid="status-reason"
          />

          {discharging && (
            <Field
              label="Discharge date"
              type="date"
              hint="Leave blank for today. Backdating is normal — care usually stops before anyone records it."
              value={effectiveAt}
              onChange={(event) => setEffectiveAt(event.target.value)}
            />
          )}

          <div className="flex items-center gap-2">
            <Button
              onClick={() => void submit()}
              pending={saving}
              {...(reason.trim()
                ? {}
                : ({ disabled: true, disabledReason: 'Enter a reason first.' } as const))}
            >
              {discharging ? 'Discharge this client' : (open as StatusMove).label}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
